import {
  Paper,
  Stack,
  CircularProgress,
  Typography,
  Box,
  List,
  ListItemButton,
  ListItemText,
  Avatar,
  Chip,
  alpha
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import Iconify from "../../../minimals/components/iconify";
import { ContactSearchResult } from "./types/businessInsights";
import { stringAvatar } from "./utils/avatarHelpers";
import { getLogo } from "../../utils/getLogo";

type SearchResultsProps = {
  results: ContactSearchResult[];
  isSearching: boolean;
  onSelect: (contact: ContactSearchResult) => void;
  onCompanyClick?: (companyId: number, companyName: string) => void;
};

export function SearchResults({ results, isSearching, onSelect, onCompanyClick }: SearchResultsProps) {
  const theme = useTheme();

  return (
    <Paper
      elevation={16}
      sx={{
        position: "absolute",
        top: "100%",
        left: 0,
        right: 0,
        mt: 1,
        maxHeight: 400,
        overflowY: "auto",
        zIndex: 5,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: `0 12px 40px ${alpha("#000", 0.12)}`,
        backdropFilter: "blur(12px)",
        background: alpha(theme.palette.background.paper, 0.98)
      }}
    >
      {isSearching ? (
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1} sx={{ p: 2 }}>
          <CircularProgress size={18} thickness={5} />
          <Typography variant="caption" fontWeight={600}>
            Searching...
          </Typography>
        </Stack>
      ) : results.length === 0 ? (
        <Stack alignItems="center" spacing={1.5} sx={{ p: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              background: alpha(theme.palette.warning.main, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `2px solid ${alpha(theme.palette.warning.main, 0.2)}`
            }}
          >
            <Iconify icon="eva:search-outline" width={24} sx={{ color: theme.palette.warning.main }} />
          </Box>
          <Stack spacing={0.5} alignItems="center">
            <Typography variant="body2" fontWeight={700} color="text.primary">
              No results found
            </Typography>
            <Typography variant="caption" color="text.secondary" fontWeight={500} textAlign="center">
              No contacts or companies match your search
            </Typography>
          </Stack>
        </Stack>
      ) : (
        <List disablePadding>
          {results.map((contact, idx) => (
            <ListItemButton
              key={`${contact.recordId ?? contact.email ?? idx}-${idx}`}
              divider={idx !== results.length - 1}
              onClick={() => onSelect(contact)}
              sx={{
                alignItems: "flex-start",
                py: 1,
                px: 1.5,
                transition: "all 0.2s ease",
                borderLeft: `2px solid transparent`,
                "&:hover": {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  borderLeftColor: theme.palette.primary.main
                }
              }}
            >
              <Avatar
                {...stringAvatar(contact.name ?? "Contact")}
                src={getLogo(contact.email?.split("@")[1])}
                sx={{
                  width: 32,
                  height: 32,
                  mr: 1.25,
                  fontSize: "0.75rem",
                  boxShadow: `0 2px 4px ${alpha("#000", 0.08)}`
                }}
              />
              <ListItemText
                primary={
                  <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                    <Typography fontWeight={700} variant="body2" sx={{ fontSize: "0.875rem" }}>
                      {contact.name ?? "Unknown"}
                    </Typography>
                    {contact.preferredName && (
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.75rem",
                          fontStyle: "italic",
                          color: theme.palette.text.secondary,
                          fontWeight: 500
                        }}
                      >
                        {contact.preferredName}
                      </Typography>
                    )}
                  </Stack>
                }
                secondary={
                  <Stack spacing={0.25} sx={{ mt: 0.25 }}>
                    {contact.companyName && (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {contact.resultType === "Contact" && contact.dimDynamicsCompanyId && onCompanyClick && (
                          <>
                            <Iconify
                              icon="eva:briefcase-outline"
                              width={11}
                              sx={{ color: theme.palette.text.secondary }}
                            />
                            <Box
                              component="span"
                              onClick={(e) => {
                                e.stopPropagation();
                                onCompanyClick(contact.dimDynamicsCompanyId!, contact.companyName!);
                              }}
                              sx={{
                                color: theme.palette.primary.main,
                                cursor: "pointer",
                                textDecoration: "none",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.25,
                                fontSize: "0.7rem",
                                fontWeight: 500,
                                "&:hover": {
                                  textDecoration: "underline"
                                }
                              }}
                            >
                              {contact.companyName}
                              <Iconify icon="eva:external-link-outline" width={10} sx={{ opacity: 0.7 }} />
                            </Box>
                          </>
                        )}
                      </Stack>
                    )}
                    {contact.email && (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Iconify icon="eva:email-outline" width={11} sx={{ color: theme.palette.text.secondary }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                          {contact.email}
                        </Typography>
                      </Stack>
                    )}
                    {(contact.city || contact.state) && (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Iconify icon="eva:pin-outline" width={11} sx={{ color: theme.palette.text.secondary }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                          {[contact.city, contact.state].filter(Boolean).join(", ")}
                        </Typography>
                      </Stack>
                    )}
                    {contact.contactRole && (
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Iconify icon="eva:person-outline" width={11} sx={{ color: theme.palette.text.secondary }} />
                        <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.7rem" }}>
                          {contact.contactRole}
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                }
              />
            </ListItemButton>
          ))}
        </List>
      )}
    </Paper>
  );
}
